import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../LanguageContext';
import {
  brokerListEmployees, brokerAddEmployee, brokerToggleEmployee, brokerResetEmployeePassword,
  brokerUpdateEmployee, brokerDeleteEmployee, brokerSetEmployeePermissions,
} from '../api';

export default function BrokerEmployees() {
  const { lang, isRTL } = useLanguage();
  const ar = lang === 'ar';
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const t = (a, e) => (ar ? a : e);

  const load = useCallback(async () => {
    const res = await brokerListEmployees();
    setEmployees(res.employees || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const errText = (code) => ({
    name_and_email_required: t('الاسم والبريد مطلوبان', 'Name and email are required'),
    weak_password: t('كلمة المرور يجب أن تكون 8 أحرف على الأقل وتحتوي حروفاً وأرقاماً', 'Password must be 8+ chars with letters and numbers'),
    email_exists: t('البريد الإلكتروني مستخدم بالفعل', 'That email already exists'),
  }[code] || t('حدث خطأ، حاول مرة أخرى', 'Something went wrong, please try again'));

  const add = async () => {
    setErr('');
    if (!form.name.trim() || !form.email.trim()) { setErr(errText('name_and_email_required')); return; }
    setSaving(true);
    const res = await brokerAddEmployee({ name: form.name.trim(), email: form.email.trim(), password: form.password });
    setSaving(false);
    if (res.error) { setErr(errText(res.error)); return; }
    setForm({ name: '', email: '', password: '' });
    load();
  };

  const toggle = async (emp) => {
    await brokerToggleEmployee(emp.id, !emp.active);
    load();
  };

  const resetPw = async (emp) => {
    const pw = window.prompt(t(`كلمة مرور جديدة لـ ${emp.name} (8 أحرف+، حروف وأرقام)`, `New password for ${emp.name} (8+ chars, letters & numbers)`));
    if (!pw) return;
    const res = await brokerResetEmployeePassword(emp.id, pw);
    if (res.error) window.alert(errText(res.error));
    else window.alert(t('تم تحديث كلمة المرور', 'Password updated'));
  };

  const editName = async (emp) => {
    const name = window.prompt(t(`الاسم الجديد لـ ${emp.name}`, `New name for ${emp.name}`), emp.name);
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === emp.name) return;
    const res = await brokerUpdateEmployee(emp.id, { name: trimmed });
    if (res.error) { window.alert(errText(res.error)); return; }
    load();
  };

  const remove = async (emp) => {
    if (!window.confirm(t(`حذف ${emp.name} نهائيًا؟ ستُلغى إحالة عملائه.`, `Delete ${emp.name}? Their leads will be unassigned.`))) return;
    const res = await brokerDeleteEmployee(emp.id);
    if (res.error) { window.alert(errText(res.error)); return; }
    load();
  };

  const togglePerm = async (emp, key) => {
    const res = await brokerSetEmployeePermissions(emp.id, { [key]: !(emp.perms && emp.perms[key]) });
    if (res.error) { window.alert(errText(res.error)); return; }
    load();
  };
  const PermChip = ({ on, label, onClick }) => (
    <button onClick={onClick} style={{ fontSize: '0.72rem', marginInlineEnd: 6, marginBottom: 4, padding: '3px 9px', borderRadius: 12, border: `1px solid ${on ? 'rgba(34,197,94,0.5)' : 'var(--border)'}`, background: on ? 'rgba(34,197,94,0.12)' : 'none', color: on ? '#16a34a' : 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
      {on ? '✓ ' : '○ '}{label}
    </button>
  );

  const cell = { padding: '10px 12px', borderBottom: '1px solid var(--border)', fontSize: '0.88rem', textAlign: isRTL ? 'right' : 'left' };
  const inputStyle = { padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.9rem' };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 900, margin: '0 auto' }}>
      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 18, marginBottom: 20 }}>
        <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 12, color: 'var(--text-primary)' }}>{t('إضافة موظف', 'Add employee')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
          <input style={inputStyle} placeholder={t('الاسم', 'Name')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input style={{ ...inputStyle, direction: 'ltr' }} placeholder={t('البريد الإلكتروني', 'Email')} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input style={{ ...inputStyle, direction: 'ltr' }} type="password" placeholder={t('كلمة المرور', 'Password')} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        </div>
        {err && <div style={{ color: 'var(--danger, #dc2626)', fontSize: '0.82rem', marginBottom: 10 }}>{err}</div>}
        <button onClick={add} disabled={saving} style={{ padding: '10px 20px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
          {t('إضافة', 'Add')}
        </button>
      </div>

      <div style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>...</div>
        ) : employees.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>{t('لا يوجد موظفون بعد', 'No employees yet')}</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-primary)' }}>
                <th style={{ ...cell, fontWeight: 700 }}>{t('الاسم', 'Name')}</th>
                <th style={{ ...cell, fontWeight: 700 }}>{t('البريد', 'Email')}</th>
                <th style={{ ...cell, fontWeight: 700 }}>{t('الحالة', 'Status')}</th>
                <th style={{ ...cell, fontWeight: 700 }}>{t('الصلاحيات', 'Permissions')}</th>
                <th style={{ ...cell, fontWeight: 700 }}></th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => (
                <tr key={e.id}>
                  <td style={cell}>{e.name}{e.broker_owner ? ` · ${t('المالك', 'Owner')}` : ''}</td>
                  <td style={{ ...cell, direction: 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{e.email}</td>
                  <td style={cell}>
                    <span style={{ color: e.active ? 'var(--success, #16a34a)' : 'var(--text-secondary)' }}>
                      {e.active ? t('نشط', 'Active') : t('معطّل', 'Inactive')}
                    </span>
                  </td>
                  <td style={cell}>
                    {e.broker_owner ? (
                      <span style={{ color: 'var(--text-secondary)', fontSize: '0.78rem' }}>{t('كامل الصلاحيات', 'Full access')}</span>
                    ) : (
                      <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        <PermChip on={!!(e.perms && e.perms['broker.add'])} label={t('إضافة عملاء', 'Add')} onClick={() => togglePerm(e, 'broker.add')} />
                        <PermChip on={!!(e.perms && e.perms['broker.edit'])} label={t('تعديل', 'Edit')} onClick={() => togglePerm(e, 'broker.edit')} />
                        <PermChip on={!!(e.perms && e.perms['broker.viewAll'])} label={t('عرض الكل', 'View all')} onClick={() => togglePerm(e, 'broker.viewAll')} />
                        <PermChip on={!!(e.perms && e.perms['broker.viewFinancials'])} label={t('العمولات والصفقات', 'Financials')} onClick={() => togglePerm(e, 'broker.viewFinancials')} />
                      </div>
                    )}
                  </td>
                  <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                    {!e.broker_owner && (
                      <>
                        <button onClick={() => toggle(e)} style={{ fontSize: '0.78rem', marginInlineEnd: 8, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                          {e.active ? t('تعطيل', 'Deactivate') : t('تفعيل', 'Activate')}
                        </button>
                        <button onClick={() => resetPw(e)} style={{ fontSize: '0.78rem', marginInlineEnd: 8, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                          {t('كلمة المرور', 'Password')}
                        </button>
                        <button onClick={() => editName(e)} style={{ fontSize: '0.78rem', marginInlineEnd: 8, padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)', background: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                          {t('تعديل الاسم', 'Edit name')}
                        </button>
                        <button onClick={() => remove(e)} style={{ fontSize: '0.78rem', padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.45)', background: 'none', color: '#ef4444', cursor: 'pointer' }}>
                          {t('حذف', 'Delete')}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
